import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  FiDownload,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiTrendingDown,
  FiTrendingUp,
  FiX,
  FiPieChart,
} from 'react-icons/fi';
import { adminApi } from '../../api';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { showToast } from '../../utils/toast';
import pnlExportDecorImage from '../../assets/copy-image.png';

function platformPnlErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    return data?.error || data?.message || err.message || 'Request failed';
  }
  if (err instanceof Error) return err.message;
  return 'Request failed. Try again.';
}

const formatUsd = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const pnlTone = (value: number) => {
  if (value > 0) return 'text-emerald-600 dark:text-emerald-400';
  if (value < 0) return 'text-rose-600 dark:text-rose-400';
  return 'text-muted-foreground';
};

async function fetchUrlAsDataUrl(url: string): Promise<string | null> {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('read'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Inline asset for downloaded HTML (file://) so the mascot / card art still renders. */
async function resolvePnlExportDecorDataUrl(): Promise<string | null> {
  const candidates: string[] = [pnlExportDecorImage as string];
  if (typeof window !== 'undefined') {
    const o = window.location.origin;
    candidates.push(`${o}/card-image.png`, `${o}/copy-image.png`, `${o}/pnl-card.png`);
  }
  for (const url of candidates) {
    const data = await fetchUrlAsDataUrl(url);
    if (data) return data;
  }
  return null;
}

type PnlRoleFilter = 'all' | 'master' | 'follower';
type PnlScopeFilter = 'today' | 'yesterday' | 'custom';

const AdminPlatformProfitLoss: React.FC = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  /** Values that drive the table API (only change when user clicks Apply filter). */
  const [appliedFilterRole, setAppliedFilterRole] = useState<PnlRoleFilter>('all');
  const [appliedFilterScope, setAppliedFilterScope] = useState<PnlScopeFilter>('today');
  const [appliedMinPnlInput, setAppliedMinPnlInput] = useState('');
  const [appliedMaxPnlInput, setAppliedMaxPnlInput] = useState('');
  /** Draft values while the filter modal is open (editing max does not hit API until Apply). */
  const [draftFilterRole, setDraftFilterRole] = useState<PnlRoleFilter>('all');
  const [draftFilterScope, setDraftFilterScope] = useState<PnlScopeFilter>('today');
  const [draftMinPnlInput, setDraftMinPnlInput] = useState('');
  const [draftMaxPnlInput, setDraftMaxPnlInput] = useState('');
  const todayIso = new Date().toISOString().split('T')[0];
  const yesterdayIso = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  })();
  const [appliedStartDate, setAppliedStartDate] = useState<string>(todayIso);
  const [appliedEndDate, setAppliedEndDate] = useState<string>(todayIso);
  const [draftStartDate, setDraftStartDate] = useState<string>(todayIso);
  const [draftEndDate, setDraftEndDate] = useState<string>(todayIso);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const appliedParsedMin = appliedMinPnlInput.trim() === '' ? undefined : Number(appliedMinPnlInput);
  const appliedParsedMax = appliedMaxPnlInput.trim() === '' ? undefined : Number(appliedMaxPnlInput);

  const combinedPeriodLabels = useMemo(() => {
    const fmt = (iso: string) =>
      new Date(`${iso}T12:00:00.000Z`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    switch (appliedFilterScope) {
      case 'today':
        return {
          ribbon: "Today's realized P&L",
          sub: 'Today — masters + followers (filled)',
          foot: 'Today net realized P&L for all masters + followers combined',
        };
      case 'yesterday':
        return {
          ribbon: "Yesterday's realized P&L",
          sub: 'Yesterday — masters + followers (filled)',
          foot: 'Yesterday net realized P&L for all masters + followers combined',
        };
      case 'custom': {
        const start = fmt(appliedStartDate);
        const end = fmt(appliedEndDate);
        const same = appliedStartDate === appliedEndDate;
        const rangeStr = same ? start : `${start} – ${end}`;
        return {
          ribbon: same ? `${start} realized P&L` : `${rangeStr} realized P&L`,
          sub: `${rangeStr} — masters + followers (filled)`,
          foot: `Net realized P&L for ${rangeStr} (all masters + followers combined)`,
        };
      }
    }
  }, [appliedFilterScope, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    if (!showFilterModal) return;
    setDraftFilterRole(appliedFilterRole);
    setDraftFilterScope(appliedFilterScope);
    setDraftMinPnlInput(appliedMinPnlInput);
    setDraftMaxPnlInput(appliedMaxPnlInput);
    setDraftStartDate(appliedStartDate);
    setDraftEndDate(appliedEndDate);
  }, [showFilterModal]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: [
      'admin',
      'platform-pnl',
      appliedFilterScope,
      appliedFilterScope === 'custom' ? appliedStartDate : '',
      appliedFilterScope === 'custom' ? appliedEndDate : '',
    ],
    queryFn: () =>
      adminApi.getPlatformPnl({
        scope: appliedFilterScope,
        startDate: appliedFilterScope === 'custom' ? appliedStartDate : undefined,
        endDate: appliedFilterScope === 'custom' ? appliedEndDate : undefined,
      }),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  const {
    data: tableData,
    isLoading: isUsersLoading,
    error: usersError,
    refetch: refetchUsers,
    isFetching: isUsersFetching,
  } = useQuery({
    queryKey: [
      'admin',
      'platform-pnl-users',
      appliedFilterRole,
      appliedFilterScope,
      appliedStartDate,
      appliedEndDate,
      appliedParsedMin ?? null,
      appliedParsedMax ?? null,
    ],
    queryFn: () =>
      adminApi.getPlatformPnlUsers({
        role: appliedFilterRole,
        scope: appliedFilterScope,
        startDate: appliedFilterScope === 'custom' ? appliedStartDate : undefined,
        endDate: appliedFilterScope === 'custom' ? appliedEndDate : undefined,
        minPnl: appliedParsedMin,
        maxPnl: appliedParsedMax,
      }),
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: 60_000,
    retry: false,
  });

  const rowKey = (row: { userId: string; role: string }) => `${row.role}:${row.userId}`;
  const rows = tableData?.rows ?? [];
  const displayedRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r:any) => {
      const name = (r.name || '').toLowerCase();
      const email = (r.email || '').toLowerCase();
      const role = (r.role || '').toLowerCase();
      // Match only this row's user (name/email/role). Do not match linked master name — that hid the real name search.
      return name.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [rows, searchQuery]);
  const selectedRows = useMemo(
    () => displayedRows.filter((r:any) => selectedIds.has(rowKey(r))),
    [displayedRows, selectedIds]
  );

  const toggleRowSelection = (key: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleKeys = displayedRows.map((r:any) => rowKey(r));
    const allSelected = visibleKeys.length > 0 && visibleKeys.every((k:any) => selectedIds.has(k));
    setSelectedIds(allSelected ? new Set() : new Set(visibleKeys));
  };

  const applyFilter = () => {
    const dMin = draftMinPnlInput.trim() === '' ? undefined : Number(draftMinPnlInput);
    const dMax = draftMaxPnlInput.trim() === '' ? undefined : Number(draftMaxPnlInput);

    if (draftMinPnlInput.trim() !== '' && dMin !== undefined && Number.isNaN(dMin)) {
      showToast('error', 'Please enter a valid minimum PnL value.');
      return;
    }
    if (draftMaxPnlInput.trim() !== '' && dMax !== undefined && Number.isNaN(dMax)) {
      showToast('error', 'Maximum PnL must be a valid number.');
      return;
    }

    if (draftFilterScope === 'custom') {
      if (!draftStartDate || !draftEndDate) {
        showToast('error', 'Please select both start and end dates.');
        return;
      }
      if (draftStartDate > draftEndDate) {
        showToast('error', 'Start date must be before or equal to end date.');
        return;
      }
    }

    // If user enters reversed bounds (common with negative ranges), normalize automatically.
    let normalizedMin = dMin;
    let normalizedMax = dMax;
    if (normalizedMin !== undefined && normalizedMax !== undefined && normalizedMin > normalizedMax) {
      const tmp = normalizedMin;
      normalizedMin = normalizedMax;
      normalizedMax = tmp;
    }

    setAppliedFilterRole(draftFilterRole);
    setAppliedFilterScope(draftFilterScope);
    if (draftFilterScope === 'today') {
      setAppliedStartDate(todayIso);
      setAppliedEndDate(todayIso);
    } else if (draftFilterScope === 'yesterday') {
      setAppliedStartDate(yesterdayIso);
      setAppliedEndDate(yesterdayIso);
    } else {
      setAppliedStartDate(draftStartDate);
      setAppliedEndDate(draftEndDate);
    }
    setAppliedMinPnlInput(normalizedMin === undefined ? '' : String(normalizedMin));
    setAppliedMaxPnlInput(normalizedMax === undefined ? '' : String(normalizedMax));
    setDraftMinPnlInput(normalizedMin === undefined ? '' : String(normalizedMin));
    setDraftMaxPnlInput(normalizedMax === undefined ? '' : String(normalizedMax));
    setSelectedIds(new Set());
    setShowFilterModal(false);
  };

  const clearFilters = () => {
    setAppliedFilterRole('all');
    setAppliedFilterScope('today');
    setAppliedStartDate(todayIso);
    setAppliedEndDate(todayIso);
    setAppliedMinPnlInput('');
    setAppliedMaxPnlInput('');
    setDraftFilterRole('all');
    setDraftFilterScope('today');
    setDraftStartDate(todayIso);
    setDraftEndDate(todayIso);
    setDraftMinPnlInput('');
    setDraftMaxPnlInput('');
    setSelectedIds(new Set());
    setShowFilterModal(false);
  };

  const exportSelectedRows = async () => {
    if (selectedRows.length === 0) {
      showToast('info', 'Please select rows before exporting.');
      return;
    }

    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const scopeUpper =
      appliedFilterScope === 'today'
        ? 'TODAY'
        : appliedFilterScope === 'yesterday'
          ? 'YESTERDAY'
          : 'CUSTOM RANGE';
    const rangeLine =
      appliedFilterScope === 'custom'
        ? `${appliedStartDate} → ${appliedEndDate}`
        : appliedStartDate;
    const exportedAt = new Date().toLocaleString();

    let decorDataUrl: string | null = null;
    try {
      decorDataUrl = await resolvePnlExportDecorDataUrl();
    } catch {
      decorDataUrl = null;
    }

    const cardsHtml = selectedRows
      .map((r:any) => {
        const pnl = r.totalPnl ?? 0;
        const pnlPositive = pnl >= 0;
        const pnlColor = pnlPositive ? '#10b981' : '#ef4444';
        const profitLabel = pnlPositive ? 'Total Profit' : 'Total Loss';
        const pnlDisplay =
          pnl === 0 ? formatUsd(0) : pnlPositive ? `+${formatUsd(pnl)}` : formatUsd(pnl);
        const isMaster = r.role === 'master';
        const rolePillBg = isMaster ? 'rgba(245, 158, 11, 0.15)' : 'rgba(59, 130, 246, 0.15)';
        const rolePillBorder = isMaster ? '1px solid rgba(245, 158, 11, 0.35)' : '1px solid rgba(59, 130, 246, 0.35)';
        const rolePillColor = isMaster ? '#fbbf24' : '#60a5fa';
        const roleWord = isMaster ? 'MASTER' : 'FOLLOWER';
        const masterLine = isMaster ? '—' : escapeHtml(r.parentMasterName || '—');
        const decorBlock = decorDataUrl
          ? `<div class="decor-wrap"><img class="decor-img" src="${decorDataUrl}" width="300" height="225" alt="" /></div>`
          : `<div class="decor-wrap decor-fallback" aria-hidden="true"></div>`;

        return `
          <section class="report-card">
            <div class="report-card-inner">
              <div class="report-head">
                <div>
                  <div class="brand-title">Trade Pilot</div>
                  <div class="brand-sub">Profit & Loss Report of selected user Download successfull</div>
                </div>
                <div class="role-pill" style="background:${rolePillBg};border:${rolePillBorder};color:${rolePillColor};">${roleWord}</div>
              </div>
              <div class="hero-name">${escapeHtml(r.name || 'Unknown User')}</div>
              <div class="hero-email">${escapeHtml(r.email || 'No email')}</div>
              <div class="scope-pill">${escapeHtml(scopeUpper)}</div>
              <div class="pnl-panel">
                <div class="pnl-panel-label">${profitLabel}</div>
                <div class="pnl-amount" style="color:${pnlColor};">${escapeHtml(pnlDisplay)}</div>
                <div class="pnl-divider"></div>
                <div class="pnl-panel-foot">
                  <span class="pnl-foot-label">Trade count</span>
                  <span class="pnl-foot-val">${escapeHtml(String((r.totalTrades || 0).toLocaleString()))}</span>
                </div>
              </div>
              ${decorBlock}
              <div class="detail-grid">
                <div>
                  <div class="d-label">Total trades</div>
                  <div class="d-val">${escapeHtml(String((r.totalTrades || 0).toLocaleString()))}</div>
                </div>
                <div>
                  <div class="d-label">Account role</div>
                  <div class="d-val">${escapeHtml(r.role.toUpperCase())}</div>
                </div>
                <div>
                  <div class="d-label">Reporting period</div>
                  <div class="d-val d-val-sm">${escapeHtml(rangeLine)}</div>
                </div>
                <div>
                  <div class="d-label">Linked master</div>
                  <div class="d-val d-val-sm">${masterLine}</div>
                </div>
              </div>
              <div class="report-footer">
                <div>
                  <div class="foot-user">${escapeHtml(r.name || 'User')}</div>
                  <div class="foot-time">${escapeHtml(exportedAt)}</div>
                </div>
                <div class="foot-brand">
                  <div class="foot-brand-name">Trade Pilot</div>
                  <div class="foot-url">www.thetradepilot.ai</div>
                </div>
              </div>
            </div>
          </section>
        `;
      })
      .join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Trade Pilot — P&L export</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              font-family: system-ui, -apple-system, Segoe UI, Inter, Arial, sans-serif;
              background: #020617;
              background-image: radial-gradient(ellipse 120% 80% at 50% -20%, #1e3a5f 0%, transparent 55%),
                radial-gradient(circle at 50% 100%, #0f172a 0%, #020617 65%);
              color: #e2e8f0;
              padding: 28px 20px 40px;
            }
            .page-header {
              max-width: 1100px;
              margin: 0 auto 22px;
              padding: 0 4px;
            }
            .page-title { font-size: 20px; font-weight: 700; margin: 0; color: #f8fafc; }
            .page-sub { margin: 6px 0 0; color: #94a3b8; font-size: 12px; line-height: 1.5; }
            .grid {
              max-width: 1100px;
              margin: 0 auto;
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
              gap: 22px;
              justify-content: center;
            }
            .report-card {
              border-radius: 20px;
              border: 1px solid rgba(71, 85, 105, 0.45);
              background: linear-gradient(165deg, rgba(15, 23, 42, 0.98) 0%, rgba(2, 6, 23, 0.99) 100%);
              box-shadow: 0 24px 48px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(148, 163, 184, 0.06) inset;
              overflow: hidden;
            }
            .report-card-inner {
              position: relative;
              padding: 22px 26px 0;
            }
            .report-card-inner::before {
              content: '';
              position: absolute;
              inset: 0 0 auto 0;
              height: 120px;
              background: radial-gradient(ellipse 90% 100% at 50% 0%, rgba(56, 189, 248, 0.08), transparent 70%);
              pointer-events: none;
            }
            .report-head {
              position: relative;
              z-index: 1;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 12px;
              margin-bottom: 14px;
            }
            .brand-title { font-size: 20px; font-weight: 700; color: #ffffff; letter-spacing: -0.02em; }
            .brand-sub {
              font-size: 10px;
              color: #94a3b8;
              text-transform: uppercase;
              letter-spacing: 0.12em;
              font-weight: 600;
              margin-top: 4px;
            }
            .role-pill {
              flex-shrink: 0;
              padding: 7px 14px;
              border-radius: 10px;
              font-size: 11px;
              font-weight: 700;
              letter-spacing: 0.06em;
            }
            .hero-name {
              position: relative;
              z-index: 1;
              font-size: 26px;
              font-weight: 700;
              color: #ffffff;
              line-height: 1.15;
              letter-spacing: -0.03em;
            }
            .hero-email {
              position: relative;
              z-index: 1;
              font-size: 13px;
              color: #94a3b8;
              margin-top: 6px;
              word-break: break-word;
            }
            .scope-pill {
              position: relative;
              z-index: 1;
              display: inline-block;
              margin-top: 12px;
              padding: 5px 12px;
              background: rgba(100, 116, 139, 0.28);
              border-radius: 8px;
              font-size: 10px;
              font-weight: 600;
              color: #cbd5e1;
              text-transform: uppercase;
              letter-spacing: 0.06em;
            }
            .pnl-panel {
              position: relative;
              z-index: 1;
              margin: 20px 0 18px;
              padding: 20px 22px;
              background: linear-gradient(135deg, rgba(30, 41, 59, 0.85) 0%, rgba(15, 23, 42, 0.92) 100%);
              border-radius: 16px;
              border: 1px solid rgba(71, 85, 105, 0.35);
            }
            .pnl-panel-label {
              font-size: 10px;
              color: #94a3b8;
              text-transform: uppercase;
              letter-spacing: 0.14em;
              font-weight: 600;
              text-align: center;
              margin-bottom: 10px;
            }
            .pnl-amount {
              text-align: center;
              font-size: 36px;
              font-weight: 800;
              letter-spacing: -0.04em;
              line-height: 1.1;
              margin-bottom: 14px;
            }
            .pnl-divider { height: 1px; background: rgba(71, 85, 105, 0.55); margin-bottom: 12px; }
            .pnl-panel-foot {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 12px;
            }
            .pnl-foot-label { font-size: 11px; color: #94a3b8; font-weight: 500; text-transform: uppercase; letter-spacing: 0.06em; }
            .pnl-foot-val { font-size: 16px; font-weight: 700; color: #e2e8f0; }
            .decor-wrap {
              position: relative;
              z-index: 1;
              display: flex;
              justify-content: center;
              padding: 8px 0 16px;
            }
            .decor-img {
              max-width: 300px;
              width: 100%;
              height: auto;
              display: block;
              border-radius: 12px;
              filter: drop-shadow(0 12px 28px rgba(0, 0, 0, 0.4));
            }
            .decor-fallback { min-height: 80px; }
            .detail-grid {
              margin: 0 -26px 0;
              padding: 18px 26px 20px;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px 22px;
              background: rgba(30, 41, 59, 0.45);
              border-top: 1px solid rgba(71, 85, 105, 0.35);
              border-bottom: 1px solid rgba(71, 85, 105, 0.35);
            }
            .d-label {
              font-size: 10px;
              color: #94a3b8;
              text-transform: uppercase;
              font-weight: 600;
              letter-spacing: 0.05em;
              margin-bottom: 5px;
            }
            .d-val { font-size: 15px; font-weight: 700; color: #e2e8f0; word-break: break-word; }
            .d-val-sm { font-size: 13px; font-weight: 600; color: #cbd5e1; line-height: 1.35; }
            .report-footer {
              position: relative;
              z-index: 1;
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 16px;
              padding: 16px 26px 20px;
            }
            .foot-user { font-size: 13px; font-weight: 600; color: #e2e8f0; }
            .foot-time { font-size: 11px; color: #94a3b8; margin-top: 3px; }
            .foot-brand { text-align: right; }
            .foot-brand-name { font-size: 14px; font-weight: 700; color: #ffffff; }
            .foot-url { font-size: 10px; color: #94a3b8; margin-top: 3px; }
            @media print {
              body { background: #fff; color: #111; }
              .report-card { break-inside: avoid; page-break-inside: avoid; box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <header class="page-header">
            <h1 class="page-title">TradePilot All Users of PNL Reports</h1>
            <p class="page-sub">Scope: ${escapeHtml(appliedFilterScope)} · ${escapeHtml(rangeLine)} · Generated ${escapeHtml(exportedAt)} · ${selectedRows.length} card(s)</p>
          </header>
          <main class="grid">${cardsHtml}</main>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform-pnl-cards-${appliedFilterScope}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('success', `${selectedRows.length} P&amp;L report card(s) downloaded.`);
  };

  if (isLoading && !data) {
    return <Loader fullScreen={false} message="Loading platform P&L…" />;
  }

  if (error || !data?.success) {
    return (
      <div className="space-y-4">
        <ErrorDisplay error={new Error(platformPnlErrorMessage(error))} />
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium hover:bg-muted/30 transition-colors"
        >
          <FiRefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    );
  }

  const uc = data.userCounts ?? { masters: 0, followers: 0 };
  const combined = data?.period ?? data?.today ?? data?.summary;
  return (
    <div className="space-y-6 sm:space-y-8 max-w-6xl mx-auto pb-8 sm:pb-10 px-1 sm:px-0">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
            Trading performance insights
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl text-sm sm:text-base">
            Aggregated realized profit and loss from master and follower trades. Updated instantly on refresh for accurate performance tracking.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:items-center gap-2 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setShowFilterModal(true)}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-2.5 text-sm font-semibold hover:bg-muted/30 transition-colors"
          >
            <FiFilter className="h-4 w-4" />
            Filter
          </button>
          <button
            type="button"
            onClick={() => void exportSelectedRows()}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-2.5 text-sm font-semibold hover:bg-muted/30 transition-colors"
          >
            <FiDownload className="h-4 w-4" />
            Export PnL
          </button>
          <button
            type="button"
            onClick={() => {
              refetch();
              refetchUsers();
            }}
            disabled={isFetching || isUsersFetching}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold shadow-sm hover:opacity-95 disabled:opacity-60 transition-opacity shrink-0"
          >
            <FiRefreshCw className={`h-4 w-4 ${(isFetching || isUsersFetching) ? 'animate-spin' : ''}`} />
            {(isFetching || isUsersFetching) ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <p className="text-xs font-semibold uppercase tracking-wide text-primary/90 -mt-4">
        {combinedPeriodLabels.ribbon}
      </p>

      <div className="grid gap-4">
        <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-violet-500/12 via-surface to-surface p-4 sm:p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="rounded-xl bg-violet-500/15 p-3 text-violet-700 dark:text-violet-400">
              <FiPieChart className="h-6 w-6" />
            </div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Combined</span>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">{combinedPeriodLabels.sub}</p>
          <p className={`mt-1 text-2xl sm:text-3xl font-bold tabular-nums ${pnlTone(combined?.platformTotalPnl ?? 0)}`}>
            {formatUsd(combined?.platformTotalPnl ?? 0)}
          </p>
          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
            {combined?.platformTotalPnl && combined.platformTotalPnl >= 0 ? (
              <FiTrendingUp className="h-4 w-4 text-emerald-500 shrink-0" />
            ) : (
              <FiTrendingDown className="h-4 w-4 text-rose-500 shrink-0" />
            )}
            <span>{combinedPeriodLabels.foot}</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-surface/80 p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-foreground">All Master & Followers Filtered Users P&L</h2>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 self-end sm:self-auto">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={selectedIds.size === 0}
              className="rounded-lg border border-border px-3 py-1.5 text-xs sm:text-sm font-semibold text-muted-foreground hover:bg-muted/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Clear all
            </button>
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border"
                checked={rows.length > 0 && rows.every((r:any) => selectedIds.has(rowKey(r)))}
                onChange={toggleSelectAll}
              />
              Select all
            </label>
          </div>
        </div>
        <div className="mb-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by this user's name, email, or role..."
              className="w-full rounded-xl border border-border bg-background pl-9 pr-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        {isUsersLoading ? (
          <Loader fullScreen={false} message="Loading filtered users..." />
        ) : usersError ? (
          <ErrorDisplay error={new Error(platformPnlErrorMessage(usersError))} />
        ) : displayedRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {searchQuery.trim() ? 'No users match your search.' : 'No users found for current filter range.'}
          </p>
        ) : (
          <>
          <div className="hidden md:block overflow-x-auto rounded-xl border border-border/60">
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col style={{ width: '11%' }} />
                <col style={{ width: '44%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead>
                <tr className="border-b border-border bg-background/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-3 px-3 align-middle">Role</th>
                  <th className="py-3 px-6 align-middle">User</th>
                  <th className="py-3 px-3 align-middle text-right">Total Trades</th>
                  <th className="py-3 px-3 align-middle text-center">PnL</th>
                  <th className="py-3 px-3 align-middle text-center">Select</th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row:any) => {
                  const key = rowKey(row);
                  const checked = selectedIds.has(key);
                  const isFollower = row.role === 'follower';
                  return (
                    <tr key={key} className="border-b border-border/60 align-middle">
                      <td className="py-3 px-3 align-middle">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium whitespace-nowrap ${row.role === 'master'
                            ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
                            : 'bg-sky-500/10 text-sky-700 dark:text-sky-300'
                            }`}
                        >
                          {row.role}
                        </span>
                      </td>
                      <td className="py-3 px-3 align-middle min-w-0">
                        <div className="flex gap-2 min-w-0">
                          <span
                            className={`mt-1 h-8 w-0.5 shrink-0 rounded-full ${isFollower ? 'bg-sky-500/50' : 'bg-transparent'
                              }`}
                            aria-hidden
                          />
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <p className="font-medium text-foreground truncate">{row.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{row.email || 'No email'}</p>
                            {isFollower && row.parentMasterName && (
                              <p className="text-xs text-muted-foreground truncate">
                                Master: {row.parentMasterName}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 align-middle tabular-nums text-center whitespace-nowrap">
                        {(row.totalTrades || 0).toLocaleString()}
                      </td>
                      <td
                        className={`py-3 px-3 align-middle tabular-nums text-center font-semibold whitespace-nowrap ${pnlTone(row.totalPnl || 0)}`}
                      >
                        {formatUsd(row.totalPnl || 0)}
                      </td>
                      <td className="py-3 px-3 align-middle">
                        <div className="flex h-full min-h-[2.5rem] items-center justify-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0 rounded border-border"
                            checked={checked}
                            onChange={() => toggleRowSelection(key)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-3">
            {displayedRows.map((row:any) => {
              const key = rowKey(row);
              const checked = selectedIds.has(key);
              const isFollower = row.role === 'follower';
              return (
                <div key={key} className="rounded-xl border border-border/70 bg-background/60 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-[11px] font-medium whitespace-nowrap ${
                        row.role === 'master'
                          ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
                          : 'bg-sky-500/10 text-sky-700 dark:text-sky-300'
                      }`}
                    >
                      {row.role}
                    </span>
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-border"
                      checked={checked}
                      onChange={() => toggleRowSelection(key)}
                    />
                  </div>
                  <div className="mt-2 min-w-0">
                    <p className="font-medium text-foreground truncate">{row.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{row.email || 'No email'}</p>
                    {isFollower && row.parentMasterName && (
                      <p className="text-xs text-muted-foreground truncate">Master: {row.parentMasterName}</p>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-border/60 bg-surface/70 p-2">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total trades</p>
                      <p className="mt-1 text-sm font-semibold tabular-nums text-foreground">
                        {(row.totalTrades || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-surface/70 p-2">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">PnL</p>
                      <p className={`mt-1 text-sm font-semibold tabular-nums ${pnlTone(row.totalPnl || 0)}`}>
                        {formatUsd(row.totalPnl || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {showFilterModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-2 sm:p-4"
          onClick={() => setShowFilterModal(false)}
        >
          <div
            className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-border/80 bg-background shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 sm:px-6 py-4 border-b border-border/70 bg-gradient-to-r from-primary/15 via-background to-background">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Filter User P&L from All Master & Follower</h3>
                </div>
                <button
                  type="button"
                  className="rounded-lg p-2 hover:bg-muted/40 transition-colors"
                  onClick={() => setShowFilterModal(false)}
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="px-5 sm:px-6 py-5 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Role
                  </label>
                  <select
                    value={draftFilterRole}
                    onChange={(e) => setDraftFilterRole(e.target.value as PnlRoleFilter)}
                    className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="all">All</option>
                    <option value="master">Masters only</option>
                    <option value="follower">Followers only</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Scope
                  </label>
                  <select
                    value={draftFilterScope}
                    onChange={(e) => setDraftFilterScope(e.target.value as PnlScopeFilter)}
                    className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="custom">Custom date range</option>
                  </select>
                </div>
              </div>

              {draftFilterScope === 'custom' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={draftStartDate}
                      onChange={(e) => setDraftStartDate(e.target.value)}
                      max={todayIso}
                      className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={draftEndDate}
                      onChange={(e) => setDraftEndDate(e.target.value)}
                      max={todayIso}
                      className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-border/70 bg-surface/70 p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      Min PnL ($)
                    </label>
                    <input
                      type="number"
                      value={draftMinPnlInput}
                      onChange={(e) => setDraftMinPnlInput(e.target.value)}
                      placeholder="e.g. 2, 10 ,-2, -50"
                      step="any"
                      className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      Max PnL ($)
                    </label>
                    <input
                      type="number"
                      value={draftMaxPnlInput}
                      onChange={(e) => setDraftMaxPnlInput(e.target.value)}
                      placeholder="e.g. 200, 1000, -500, -1000"
                      className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                  Tips: Profit & Losses can be filtered using positive & negative values (e.g. 2, 500, -30, -1000 , -300). Zero Pnl  is not show in Filtered Users P&L above . Use Today/Yesterday presets or define a custom date range for precise results.
                </p>
              </div>

              <div className="pt-1 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-xl border border-border px-4 py-2.5 text-sm font-semibold hover:bg-muted/30 transition-colors"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={applyFilter}
                  className="rounded-xl bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold shadow-sm hover:opacity-95 transition-opacity"
                >
                  Apply filter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlatformProfitLoss;
